// material-ui
import {
  Button,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { cloneDeep } from 'lodash-es';
import { MTableToolbar } from 'material-table';
import moment from 'moment';
import { highlight, languages } from 'prismjs/components/prism-core';
import React from 'react';
import Editor from 'react-simple-code-editor';

import { InventoryForecastEventTypes, track } from '~/src/dash/analytics/analytics';
// api
import { getChannelGroupsPromise } from '~/src/dash/api/channel-group';
import { getSmallSites } from '~/src/dash/api/channels/channels_methods';
import { ISmallSite } from '~/src/dash/api/channels/channels_models';
import { IAnalytics, IChannelGroup } from '~/src/dash/api/console-api';
import { convertPriorityToBidderTier } from '~/src/dash/api/headerbidders/utils_headerbidder';
import {
  getInventoryForecast,
  IBookedCampaignImpressionInfo,
  IBookedDealImpressionInfo,
  IForecastInventoryResponse,
  transformForecastInventoryResponse,
} from '~/src/dash/api/inventory-forecast';
import { convertBidderTierToCustom, publisherHasCustomBidderTiers } from '~/src/dash/api/publisher';
// components
import {
  DateTimeRangeComponent,
  IDateRangeChangeEvent,
} from '~/src/dash/components/datepicker/date-time-range-component';
import { IForecastRowData, IListTableColumn, ListTable } from '~/src/dash/components/list-table';
import { MaterialSearchDropdownMulti } from '~/src/dash/components/material-search-dropdown-multi';
import { ModelSmallButtonStyle } from '~/src/dash/components/model-medium-button';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
// other
import { genTimezoneList } from '~/src/dash/globals/timezone';
import { Panel, Panels } from '~/src/dash/publicaUI';
import { IStore } from '~/src/dash/reducers/store';
// views
import { IMenuList } from '~/src/dash/types/menuList';
import {
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
// utils
import { ChipCompleted } from '~/src/dash/utils/campaign-status';
import { publicaColors } from '~/src/dash/utils/colors';
import { getFutureEndDate, toStartDate } from '~/src/dash/utils/date';
import { decorate } from '~/src/dash/utils/decorator';
import { deviceEnvironmentSubTypesAsIMenu } from '~/src/dash/utils/device-environments';
import { createAllDeviceTypesAsEnumMenuList } from '~/src/dash/utils/devices';
import { noopVoid } from '~/src/dash/utils/noop';
import { checkUserInfoMasked, hasRoleSuperUser } from '~/src/dash/utils/roles';
import { routeName } from '~/src/dash/utils/routes';
import { isSamsungPublisher } from '~/src/dash/utils/ui';
import { getBiddersList } from '~/src/dash/views/common/dashboards-consts';
import { LoadingView } from '~/src/dash/views/loading-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {}

interface IState {
  updating: boolean;
  loading: boolean;
  forecastInventoryResponse: IForecastInventoryResponse;
  forecastQuery: string;
  startDate: moment.Moment;
  endDate: moment.Moment;
  forecastDuration: number;
  // dropdown lists
  timezonesList: IMenuList;
  biddersMenuList: IMenuList;
  channelGroupsMenuList: IMenuList;
  channelsMenuList: IMenuList;
  deviceTypesList: IMenuList;
  deviceSubtypesList: IMenuList;
  periodsList: IMenuList;
  // forecast tables information
  availableGroupBy: string;
  availableColumns: IListTableColumn[];
  availableData: IForecastRowData[];
  bookedGroupBy: string;
  bookedColumns: IListTableColumn[];
  bookedData: IForecastRowData[];
  // adjustment factors for cpm / imps for the "total" row
  totalAvailableCPMFactor: number;
  totalAvailableImpressionFactor: number;
  totalBookedCPMFactor: number;
  totalBookedImpressionFactor: number;
  // error messaging
  snackbarMessage: string;
  snackbarError: boolean;
}

interface IForecastParams {
  bidderIDs: string;
  siteIDs: string;
  startDate: string;
  endDate: string;
  timezone: string;
  deviceTypes: string;
  deviceSubtypes: string;
}

interface IForecastMap {
  [key: string]: IForecastRowData;
}

export type ForecastTableName = string;
export const AvailableTable: ForecastTableName = 'Estimated Available Forecast';
export const BookedTable: ForecastTableName = 'Estimated Booked Forecast';

export type ForecastPresetPeriod = string;
export const CustomDays: ForecastPresetPeriod = 'Custom';
export const Last7Days: ForecastPresetPeriod = 'Last 7 Days';
export const Last28Days: ForecastPresetPeriod = 'Last 28 Days';
export const Last90Days: ForecastPresetPeriod = 'Last 90 Days';

export type GroupByOption = string;
export const GroupByChannel: GroupByOption = 'Channel';
export const GroupByNone: GroupByOption = 'None';
export const GroupByLineItem: GroupByOption = 'Line Item';
export const GroupByPGDeal: GroupByOption = 'PG Deal';
export const GroupByTier: GroupByOption = 'Tier';

export const InventoryForecastView = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: () => ({
      button: ModelSmallButtonStyle,
      rowTitle: {
        fontSize: 14,
        fontWeight: 500,
      },
      toolbarRow: {
        marginLeft: -10,
        width: '100%',
        marginTop: 5,
      },
      dropdownRow: {
        maxWidth: 400,
      },
      tableTitle: {
        marginLeft: 10,
        fontSize: 18,
        fontWeight: 500,
      },
      boldText: {
        fontSize: 14,
        fontWeight: 500,
      },
    }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      updating: false,
      loading: false,
      forecastInventoryResponse: {} as IForecastInventoryResponse,
      forecastQuery: '',
      timezonesList: {} as IMenuList,
      startDate: getFutureEndDate(this.props.session.publisher.Timezone || 'UTC', -9, 'day'),
      endDate: getFutureEndDate(this.props.session.publisher.Timezone || 'UTC', -2, 'day'),
      forecastDuration: 7,
      // dropdown lists
      biddersMenuList: {} as IMenuList,
      channelGroupsMenuList: {} as IMenuList,
      channelsMenuList: {} as IMenuList,
      deviceTypesList: createAllDeviceTypesAsEnumMenuList(),
      deviceSubtypesList: isSamsungPublisher(this.props.session.publisher.ID)
        ? deviceEnvironmentSubTypesAsIMenu(
            this.props.session.publisher.ID,
            hasRoleSuperUser(this.props.user.user),
          )
        : ({} as IMenuList),
      periodsList: {} as IMenuList,
      availableGroupBy: GroupByNone,
      // forecast tables information
      availableColumns: [] as IListTableColumn[],
      availableData: [] as IForecastRowData[],
      bookedGroupBy: GroupByNone,
      bookedColumns: [] as IListTableColumn[],
      bookedData: [] as IForecastRowData[],
      // adjustment factors for cpm / imps for the "total" row
      totalAvailableCPMFactor: 100,
      totalAvailableImpressionFactor: 100,
      totalBookedCPMFactor: 100,
      totalBookedImpressionFactor: 100,
      // error messaging
      snackbarMessage: '',
      snackbarError: false,
    };

    componentDidMount() {
      this.createMenuLists();
    }

    createMenuLists = () => {
      const { session } = this.props;
      this.setState(
        {
          loading: true,
        },
        async () => {
          getBiddersList(session, this.props.user).then((biddersList) => {
            const newBiddersMenuList: IMenuList = {};
            for (const bidder of Object.values(biddersList.Bidders)) {
              const bidderName = checkUserInfoMasked(this.props.user.user)
                ? `demo_bidder_${bidder.ID}`
                : bidder.Name;
              newBiddersMenuList[bidder.ID] = {
                title: bidderName,
                selected: false,
                ID: bidder.ID.toString(),
              };
            }
            this.setState({
              biddersMenuList: biddersList.Bidders,
            });
          });

          // sites
          const [smallSites, err] = await getSmallSites(this.props.session.publisher.ID);
          if (err) {
            this.setState({
              snackbarMessage: 'Loading sites failed.',
              snackbarError: true,
              loading: false,
            });
          }

          const channelsMenuList: IMenuList = (smallSites || []).reduce(
            (acc: IMenuList, site: ISmallSite) => {
              const siteName = checkUserInfoMasked(this.props.user.user)
                ? `demo_site_${site.ID}`
                : site.Name;
              acc[site.ID] = {
                title: siteName,
                selected: false,
                ID: site.ID.toString(),
              };
              return acc;
            },
            {},
          );

          getChannelGroupsPromise({
            publisherID: this.props.session.publisher.ID,
          })
            .then(({ channelGroups }: { channelGroups: IChannelGroup[] }) => {
              // timezones
              const timezone = session.publisher.Timezone;
              const timezonesList = genTimezoneList(timezone);

              // channel groups
              const channelGroupsMenuList: IMenuList = {};
              channelGroups.forEach((cg: IChannelGroup) => {
                const cgName = checkUserInfoMasked(this.props.user.user)
                  ? `demo_cg_${cg.ID}`
                  : `[${cg.ID}] ${cg.Name}`;
                channelGroupsMenuList[cg.ID] = {
                  title: cgName,
                  selected: false,
                  ID: cg.ID.toString(),
                  channelGroup: cg,
                };
              });

              // channel groups
              const periodsList: IMenuList = {};
              [Last7Days, Last28Days, Last90Days, CustomDays].forEach(
                (period: string, index: number) => {
                  periodsList[index] = {
                    title: period,
                    selected: period === Last7Days,
                    ID: index.toString(),
                  };
                },
              );

              this.setState({
                timezonesList,
                channelsMenuList,
                channelGroupsMenuList,
                periodsList,
                loading: false,
              });
            })
            .catch(() => {
              this.setState({
                snackbarMessage: 'Loading channel group settings failed.',
                snackbarError: true,
                loading: false,
              });
            });
        },
      );
    };

    generateForecastParams = (): IForecastParams => {
      const {
        biddersMenuList,
        channelsMenuList,
        channelGroupsMenuList,
        timezonesList,
        deviceTypesList,
        deviceSubtypesList,
        startDate,
        endDate,
      } = this.state;

      const bidderIDs = [] as string[];
      for (const bidderItem of Object.values(biddersMenuList)) {
        if (bidderItem.selected) {
          bidderIDs.push(bidderItem.ID.toString());
        }
      }

      const channelIDs = [] as string[];
      for (const item of Object.values(channelsMenuList)) {
        if (item.selected) {
          channelIDs.push(item.ID.toString());
        }
      }

      for (const item of Object.values(channelGroupsMenuList)) {
        if (item.selected && item.channelGroup) {
          channelIDs.push(...item.channelGroup.ChannelIDs.split(','));
          break;
        }
      }

      let timezone = 'UTC';
      for (const item of Object.values(timezonesList)) {
        if (item.selected) {
          timezone = item.title;
          break;
        }
      }

      const deviceTypes = [] as string[];
      for (const item of Object.values(deviceTypesList)) {
        if (item.selected) {
          deviceTypes.push(item.ID.toString());
        }
      }

      const deviceSubtypes = [] as string[];
      for (const item of Object.values(deviceSubtypesList)) {
        if (item.selected) {
          deviceSubtypes.push(item.ID.toString());
        }
      }

      return {
        bidderIDs: bidderIDs.join(','),
        siteIDs: channelIDs.join(','),
        startDate: startDate.format('YYYY-MM-DDTHH:mm:ss'),
        endDate: endDate.format('YYYY-MM-DDTHH:mm:ss'),
        timezone,
        deviceTypes: deviceTypes.join(','),
        deviceSubtypes: deviceSubtypes.join(','),
      } as IForecastParams;
    };

    trackEvent = (
      eventTitle: InventoryForecastEventTypes,
      meta: { [key: string]: string },
    ): void => {
      const is_super_user = hasRoleSuperUser(this.props.user.user);
      track(
        eventTitle,
        Object.assign(
          {
            is_super_user,
            userID: this.props.user.user.ID,
            publisherID: this.props.session.publisher.ID,
          },
          meta,
        ),
        this.props.user.user,
      );
    };

    getInventoryForecast = (): void => {
      const { updating } = this.state;
      if (updating) {
        return;
      }
      const { bidderIDs, siteIDs, startDate, endDate, timezone, deviceTypes, deviceSubtypes } =
        this.generateForecastParams();
      this.setState(
        {
          updating: true,
          forecastDuration: this.state.endDate.diff(this.state.startDate, 'days'),
        },
        () => {
          this.trackEvent('Inventory - Forecasting Tool', {
            bidderIDs,
            siteIDs,
            startDate,
            endDate,
            timezone,
            deviceTypes,
            deviceSubtypes,
          });
          getInventoryForecast(
            bidderIDs,
            siteIDs,
            startDate,
            endDate,
            timezone,
            deviceTypes,
            deviceSubtypes,
            this.props.session.publisher.ID,
          )
            .then((resp) => {
              const forecastInventoryResponse = transformForecastInventoryResponse(resp.parsedBody);
              if (forecastInventoryResponse.Success && forecastInventoryResponse.Analytics) {
                for (const datum of forecastInventoryResponse.Analytics) {
                  datum.BidderTier = convertPriorityToBidderTier(datum.BidderPriority);
                }
                this.setState(
                  {
                    totalAvailableCPMFactor: 100,
                    totalAvailableImpressionFactor: 100,
                    totalBookedCPMFactor: 100,
                    totalBookedImpressionFactor: 100,
                    forecastQuery: forecastInventoryResponse.Query,
                    forecastInventoryResponse,
                    updating: false,
                  },
                  () => {
                    this.generateAvailableDataAndColumns();
                    this.generateBookedDataAndColumns();
                  },
                );
              } else {
                this.setState({
                  snackbarError: true,
                  snackbarMessage: 'Error creating inventory forecast',
                  updating: false,
                });
              }
            })
            .catch(() => {
              this.setState({
                snackbarError: true,
                snackbarMessage: 'Error creating inventory forecast',
                updating: false,
              });
            });
        },
      );
    };

    // -------- HANDLER / HELPER FUNCTIONS -------- //

    handleCloseSnackbar = (): void => {
      this.setState({
        snackbarMessage: '',
        snackbarError: false,
      });
    };

    // start and end date must be between 300 days ago and today for valid forecast
    onDateChangeValidator = ({ start, end }: IDateRangeChangeEvent): boolean => {
      const duration = toStartDate(moment(end)).diff(toStartDate(moment(start)), 'days');
      if (toStartDate(moment(end)).isSameOrAfter(toStartDate(moment()))) {
        this.setState({
          snackbarMessage: 'Forecast end date cannot be today or after',
          snackbarError: true,
        });
        return false;
      } else if (
        toStartDate(moment(start)).isSameOrBefore(toStartDate(moment().add(-365, 'days')))
      ) {
        this.setState({
          snackbarMessage: 'Forecast start date cannot be before 1 year ago',
          snackbarError: true,
        });
        return false;
      } else if (duration > 90) {
        this.setState({
          snackbarMessage: 'Forecast duration cannot be longer than 90 days',
          snackbarError: true,
        });
        return false;
      } else if (start.isAfter(end)) {
        this.setState({
          snackbarMessage: 'Start date cannot be after end date',
          snackbarError: true,
        });
        return false;
      } else {
        return true;
      }
    };

    handleDateChange = ({ start, end }: IDateRangeChangeEvent) => {
      const periodsList = cloneDeep(this.state.periodsList);
      for (const period of Object.values(periodsList)) {
        period.selected = period.title === 'Custom';
      }
      this.setState({
        startDate: start,
        endDate: end,
        periodsList,
      });
    };

    handlePeriodMenuAcceptClick = (periodList: IMenuList): void => {
      let selectedPeriod = Last7Days;
      for (const item of Object.values(periodList)) {
        if (item.selected) {
          selectedPeriod = item.title;
          break;
        }
      }
      let startDate = moment(this.state.startDate);
      let endDate = moment(this.state.endDate);
      if (selectedPeriod === Last28Days) {
        startDate = getFutureEndDate(this.props.session.publisher.Timezone || 'UTC', -30, 'day');
        endDate = getFutureEndDate(this.props.session.publisher.Timezone || 'UTC', -2, 'day');
      } else if (selectedPeriod === Last90Days) {
        startDate = getFutureEndDate(this.props.session.publisher.Timezone || 'UTC', -92, 'day');
        endDate = getFutureEndDate(this.props.session.publisher.Timezone || 'UTC', -2, 'day');
      } else if (selectedPeriod === Last7Days) {
        startDate = getFutureEndDate(this.props.session.publisher.Timezone || 'UTC', -9, 'day');
        endDate = getFutureEndDate(this.props.session.publisher.Timezone || 'UTC', -2, 'day');
      }
      this.setState({
        startDate,
        endDate,
      });
    };

    handleMenuAcceptClick = (_values: IMenuList, menuType: string): void => {
      if (menuType === 'Channel Groups') {
        const channelsMenuList = cloneDeep(this.state.channelsMenuList);
        for (const item of Object.values(channelsMenuList)) {
          item.selected = false;
        }
        this.setState({
          channelsMenuList,
        });
      } else if (menuType === 'Channels') {
        const channelGroupsMenuList = cloneDeep(this.state.channelGroupsMenuList);
        for (const item of Object.values(channelGroupsMenuList)) {
          item.selected = false;
        }
        this.setState({
          channelGroupsMenuList,
        });
      } else if (menuType === 'Bidders') {
        const biddersMenuList = cloneDeep(this.state.biddersMenuList);
        for (const item of Object.values(biddersMenuList)) {
          item.selected = false;
        }
        this.setState({
          biddersMenuList,
        });
      }
    };

    handleChangeGroupBy = (
      ev: React.ChangeEvent<{ name: string; value: string }>,
      tableName: ForecastTableName,
    ) => {
      this.setState(
        {
          availableGroupBy:
            tableName === AvailableTable ? ev.target.value : this.state.availableGroupBy,
          bookedGroupBy: tableName === BookedTable ? ev.target.value : this.state.bookedGroupBy,
        },
        () => {
          this.generateAvailableDataAndColumns();
          this.generateBookedDataAndColumns();
        },
      );
    };

    handleChangeItem = (
      id_str: string,
      field: string,
      value: any,
      tableName: ForecastTableName,
    ) => {
      const isAvailableTable = tableName === AvailableTable;
      const data = cloneDeep(isAvailableTable ? this.state.availableData : this.state.bookedData);
      const id = parseInt(id_str, 10);

      if (['CPMFactor', 'ImpressionFactor'].includes(field)) {
        const factor = parseInt(value, 10) || 0;
        if (id === data.length) {
          // if changing factors for total row (which is given the id === data.length)
          if (isAvailableTable) {
            if (field === 'CPMFactor') {
              this.setState({ totalAvailableCPMFactor: factor });
            } else if (field === 'ImpressionFactor') {
              this.setState({ totalAvailableImpressionFactor: factor });
            }
          } else {
            if (field === 'CPMFactor') {
              this.setState({ totalBookedCPMFactor: factor });
            } else if (field === 'ImpressionFactor') {
              this.setState({ totalBookedImpressionFactor: factor });
            }
          }
        } else {
          // if changing factors for any non-total row
          for (const datum of data) {
            if (id === datum.ID) {
              if (field === 'CPMFactor') {
                datum.CPMFactor = factor;
              } else if (field === 'ImpressionFactor') {
                datum.ImpressionFactor = factor;
              }
            }
          }
          if (isAvailableTable) {
            this.setState({
              availableData: data,
            });
          } else {
            this.setState({
              bookedData: data,
            });
          }
        }
      } else if (field === 'MarkedAsBooked') {
        // switch the booked status of a row and move the datum from one table to the other
        const availableData = cloneDeep(this.state.availableData);
        const bookedData = cloneDeep(this.state.bookedData);
        let chosenIdx = -1;

        if (isAvailableTable) {
          for (let i = 0; i < availableData.length; i++) {
            if (availableData[i].ID === id) {
              chosenIdx = i;
            }
          }
          if (chosenIdx >= 0) {
            const datumToMove = availableData.splice(chosenIdx, 1)[0];
            datumToMove.ID = bookedData.length;
            datumToMove.MarkedAsBooked = true;
            bookedData.push(datumToMove);
          }
        } else {
          for (let i = 0; i < bookedData.length; i++) {
            if (bookedData[i].ID === id) {
              chosenIdx = i;
            }
          }
          if (chosenIdx >= 0) {
            const datumToMove = bookedData.splice(chosenIdx, 1)[0];
            datumToMove.ID = availableData.length;
            datumToMove.MarkedAsBooked = false;
            availableData.push(datumToMove);
          }
        }
        this.setState({
          availableData,
          bookedData,
        });
      }
    };

    // -------- RENDER FUNCTIONS -------- //

    renderForecastSettings = () => {
      const {
        timezonesList,
        biddersMenuList,
        channelsMenuList,
        channelGroupsMenuList,
        deviceTypesList,
        deviceSubtypesList,
        periodsList,
        startDate,
        endDate,
      } = this.state;
      const { classes } = this.props;

      let timezone = 'UTC';
      for (const item of Object.values(timezonesList)) {
        if (item.selected) {
          timezone = item.title;
          break;
        }
      }

      return (
        <Grid item style={{ width: '100%' }}>
          <Grid
            container
            alignItems='flex-start'
            justify='flex-start'
            direction='column'
            spacing={4}
          >
            {/* Period Selectors */}
            <Grid item style={{ width: '90%' }}>
              <Typography style={{ fontSize: 14 }}>
                {
                  'Get an estimate of available impressions per tiers & channels based on the chosen historical data range and see booked impressions based on Line items.'
                }
              </Typography>
            </Grid>
            <Grid item style={{ width: '100%', marginBottom: -10 }}>
              <Grid container alignItems='center' justify='flex-start' direction='row'>
                <Grid item xs={2}>
                  <Typography className={this.props.classes.rowTitle} style={{ marginTop: 18 }}>
                    Historical Data Range
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                  <Grid container alignItems='center' spacing={2}>
                    <Grid item style={{ width: 200, marginTop: 18 }}>
                      <MaterialSearchDropdownMulti
                        menuTitle='Preset Periods'
                        selectAll={false}
                        list={periodsList}
                        onChange={(periodsList: IMenuList) => {
                          this.setState({
                            periodsList,
                          });
                        }}
                        handleMenuAcceptClick={this.handlePeriodMenuAcceptClick}
                        enableMultiSelect={false}
                        maxHeight={350}
                        fontSize='14px'
                        enableFixed={true}
                      />
                    </Grid>
                    <Grid item>
                      <DateTimeRangeComponent
                        enableClearSelection={true}
                        timezone={timezone}
                        start={startDate}
                        end={endDate}
                        minDate={getFutureEndDate(
                          this.props.session.publisher.Timezone || 'UTC',
                          -300,
                          'day',
                        )}
                        maxDateExtended={-2}
                        onChange={this.handleDateChange}
                        onChangeValidator={this.onDateChangeValidator}
                        disableTime={true}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            {/* Timezone Dropdown */}
            <Grid item style={{ width: '100%' }}>
              <Grid container alignItems='center' justify='flex-start' direction='row'>
                <Grid item xs={2}>
                  <Typography className={this.props.classes.rowTitle}>Timezone</Typography>
                </Grid>
                <Grid item xs={10} className={classes.dropdownRow}>
                  <MaterialSearchDropdownMulti
                    selectAll={false}
                    menuTitle='Timezones'
                    list={timezonesList}
                    disabled={false}
                    onChange={(timezonesList: IMenuList) => {
                      this.setState({
                        timezonesList,
                      });
                    }}
                    handleMenuAcceptClick={noopVoid}
                    isNoneSelectedAllSelected={false}
                    enableMultiSelect={false}
                  />
                </Grid>
              </Grid>
            </Grid>
            {/* Channels Dropdown */}
            <Grid item style={{ width: '100%' }}>
              <Grid container alignItems='center' justify='flex-start' direction='row'>
                <Grid item xs={2}>
                  <Typography className={this.props.classes.rowTitle}>Channels</Typography>
                </Grid>
                <Grid item xs={10} className={classes.dropdownRow}>
                  <MaterialSearchDropdownMulti
                    selectAll={false}
                    menuTitle='Channels'
                    list={channelsMenuList}
                    disabled={false}
                    onChange={(channelsMenuList: IMenuList) => {
                      this.setState({
                        channelsMenuList,
                      });
                    }}
                    handleMenuAcceptClick={noopVoid}
                    isNoneSelectedAllSelected={false}
                    enableMultiSelect={true}
                    selectLimit={100}
                    menuTitleOverride='Select up to 100'
                    showPrefixID
                  />
                </Grid>
              </Grid>
            </Grid>
            {/* Channel Groups Dropdown */}
            <Grid item style={{ width: '100%' }}>
              <Grid container alignItems='center' justify='flex-start' direction='row'>
                <Grid item xs={2}>
                  <Typography className={this.props.classes.rowTitle}>Channel Groups</Typography>
                </Grid>
                <Grid item xs={10} className={classes.dropdownRow}>
                  <MaterialSearchDropdownMulti
                    selectAll={false}
                    menuTitle='Channel Group'
                    list={channelGroupsMenuList}
                    disabled={false}
                    onChange={(channelGroupsMenuList: IMenuList) => {
                      this.setState({
                        channelGroupsMenuList,
                      });
                    }}
                    handleMenuAcceptClick={noopVoid}
                    isNoneSelectedAllSelected={false}
                    enableMultiSelect={true}
                    selectLimit={1}
                    menuTitleOverride='Select a Channel Group'
                  />
                </Grid>
              </Grid>
            </Grid>
            {/* Bidders Dropdown */}
            <Grid item style={{ width: '100%' }}>
              <Grid container alignItems='center' justify='flex-start' direction='row'>
                <Grid item xs={2}>
                  <Typography className={this.props.classes.rowTitle}>Bidders</Typography>
                </Grid>
                <Grid item xs={10} className={classes.dropdownRow}>
                  <MaterialSearchDropdownMulti
                    selectAll={false}
                    menuTitle='Bidder'
                    list={biddersMenuList}
                    disabled={false}
                    onChange={(biddersMenuList: IMenuList) => {
                      this.setState({
                        biddersMenuList,
                      });
                    }}
                    handleMenuAcceptClick={noopVoid}
                    isNoneSelectedAllSelected={false}
                    enableMultiSelect={true}
                    selectLimit={100}
                    menuTitleOverride='Select up to 100'
                    showPrefixID
                  />
                </Grid>
              </Grid>
            </Grid>
            {/* Device Types Dropdown */}
            <Grid item style={{ width: '100%' }}>
              <Grid container alignItems='center' justify='flex-start' direction='row'>
                <Grid item xs={2}>
                  <Typography className={this.props.classes.rowTitle}>Device Types</Typography>
                </Grid>
                <Grid item xs={10} className={classes.dropdownRow}>
                  <MaterialSearchDropdownMulti
                    menuTitle='Device Types'
                    selectAll={false}
                    list={deviceTypesList}
                    onChange={(deviceTypesList: IMenuList) => {
                      this.setState({
                        deviceTypesList,
                      });
                    }}
                    handleMenuAcceptClick={noopVoid}
                    enableMultiSelect={true}
                    onlyShowNumberWhenClosed={true}
                    maxHeight={350}
                    enableFixed={true}
                  />
                </Grid>
              </Grid>
            </Grid>
            {isSamsungPublisher(this.props.session.publisher.ID) && (
              <Grid item style={{ width: '100%' }}>
                <Grid container alignItems='center' justify='flex-start' direction='row'>
                  <Grid item xs={2}>
                    <Typography className={this.props.classes.rowTitle}>Device Subtypes</Typography>
                  </Grid>
                  <Grid item xs={10} className={classes.dropdownRow}>
                    <MaterialSearchDropdownMulti
                      menuTitle='Device Subtypes'
                      selectAll={false}
                      list={deviceSubtypesList}
                      onChange={(deviceSubtypesList: IMenuList) => {
                        this.setState({
                          deviceSubtypesList,
                        });
                      }}
                      handleMenuAcceptClick={noopVoid}
                      enableMultiSelect={true}
                      onlyShowNumberWhenClosed={true}
                      maxHeight={350}
                      enableFixed={true}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
      );
    };

    renderAvailableToolbar = (props: any) => {
      const { availableGroupBy, forecastDuration } = this.state;
      const { classes } = this.props;
      const copyProps = {
        ...props,
        searchFieldStyle: { ...props.searchFieldStyle, width: 250, marginRight: 15 },
      };

      return (
        <Grid item style={{ width: '100%' }}>
          <Grid container direction='row' justify='space-between' alignItems='center'>
            <Grid item>
              <Grid container direction='row' spacing={2} alignItems='center'>
                <Grid item>
                  <Typography className={classes.tableTitle}>
                    Estimated Available Impressions
                  </Typography>
                </Grid>
                <Grid item>
                  <ChipCompleted clickable={false} label={`Next ${forecastDuration} Days`} />
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container alignItems='center' spacing={2}>
                <Grid item>
                  <Typography className={classes.boldText}>Group By</Typography>
                </Grid>
                <Grid item>
                  <Select
                    value={availableGroupBy}
                    name='availableGroupBy'
                    onChange={(ev: React.ChangeEvent<{ name: string; value: string }>) =>
                      this.handleChangeGroupBy(ev, AvailableTable)
                    }
                  >
                    {[GroupByNone, GroupByChannel, GroupByTier].map((type) => {
                      return (
                        <MenuItem value={type}>
                          <Typography style={{ fontSize: 14 }}>{type}</Typography>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <MTableToolbar {...copyProps} />
            </Grid>
          </Grid>
        </Grid>
      );
    };

    generateAvailableDataAndColumns = (): void => {
      const { forecastInventoryResponse, availableGroupBy } = this.state;
      const availableColumns: IListTableColumn[] = [
        { field: 'SiteName', title: 'Site Name', renderType: 'normal', width: '15%' },
        { field: 'SubCategory', title: 'Sub Category', renderType: 'normal', width: '15%' },
        { field: 'MarkedAsBooked', title: 'Booked', renderType: 'markedflag' },
        { field: 'Impressions', title: 'Impressions', renderType: 'bignumber' },
        { field: 'eCPM', title: 'eCPM', renderType: 'currency' },
        { field: 'Revenue', title: 'Revenue', renderType: 'currency' },
        { field: 'CPMFactor', title: 'CPM Adjustment%', renderType: 'editable' },
        { field: 'ImpressionFactor', title: 'Impression Adjustment%', renderType: 'editable' },
      ];
      let availableData = [] as IForecastRowData[];

      // when grouping by tier or site, use a map to aggregate, then convert map to array at the end
      if (forecastInventoryResponse.Analytics) {
        if (availableGroupBy === GroupByTier) {
          const tierMap = {} as IForecastMap;
          // ids going negative so theres no overlap between booked and available
          let id = -1;
          for (const datum of forecastInventoryResponse.Analytics) {
            if (tierMap[datum.BidderTier]) {
              tierMap[datum.BidderTier].Impressions += datum.Impressions;
              if (tierMap[datum.BidderTier].Revenue !== undefined) {
                // @ts-ignore | we checked not undefined above in the `if` but the lint is not smart enough
                tierMap[datum.BidderTier].Revenue += datum.Revenue;

                if (tierMap[datum.BidderTier].eCPM !== undefined) {
                  // @ts-ignore | we checked not undefined above in the `if` but the lint is not smart enough
                  tierMap[datum.BidderTier].eCPM +=
                    (tierMap[datum.BidderTier].Revenue / tierMap[datum.BidderTier].Impressions) *
                    1000;
                }
              }
            } else {
              tierMap[datum.BidderTier] = {
                ID: id,
                Name: '',
                SiteName: 'All Channels',
                Active: true,
                SubCategory: publisherHasCustomBidderTiers(this.props.session.publisher)
                  ? `Tier ${datum.BidderTier} - ` +
                    convertBidderTierToCustom(this.props.session.publisher, datum.BidderTier)
                  : `Tier ${datum.BidderTier}`,
                Impressions: datum.Impressions,
                SiteShareOfImpressions: 0,
                eCPM: 0,
                Revenue: datum.Revenue,
                CPMFactor: 100,
                ImpressionFactor: 100,
                EnableEditable: false,
                MarkedAsBooked: false,
              };
              id--;
            }
          }
          availableData = Object.values(tierMap).map((row) => row);
        } else if (availableGroupBy === GroupByChannel) {
          const siteMap = {} as IForecastMap;
          let id = -1;
          for (const datum of forecastInventoryResponse.Analytics) {
            if (siteMap[datum.SiteID]) {
              siteMap[datum.SiteID].Impressions += datum.Impressions;
              if (siteMap[datum.SiteID].Revenue !== undefined) {
                // @ts-ignore | we checked not undefined above in the `if` but the lint is not smart enough
                siteMap[datum.SiteID].Revenue += datum.Revenue;

                if (siteMap[datum.SiteID].eCPM !== undefined) {
                  // @ts-ignore | we checked not undefined above in the `if` but the lint is not smart enough
                  siteMap[datum.SiteID].eCPM +=
                    (siteMap[datum.SiteID].Revenue / siteMap[datum.SiteID].Impressions) * 1000;
                }
              }
            } else {
              siteMap[datum.SiteID] = {
                ID: id,
                Name: '',
                SiteName: checkUserInfoMasked(this.props.user.user)
                  ? `demo_site_${datum.SiteID}`
                  : datum.SiteName,
                Active: true,
                SubCategory: 'All Tiers',
                Impressions: datum.Impressions,
                SiteShareOfImpressions: 0,
                eCPM: datum.AvgImpECPM,
                Revenue: datum.Revenue,
                CPMFactor: 100,
                ImpressionFactor: 100,
                EnableEditable: false,
                MarkedAsBooked: false,
              };
              id--;
            }
          }
          availableData = Object.values(siteMap).map((row) => row);
        } else if (availableGroupBy === GroupByNone) {
          let id = -1;
          availableData = forecastInventoryResponse.Analytics.map((datum: IAnalytics) => {
            const row = {
              ID: id,
              Name: '',
              SiteName: checkUserInfoMasked(this.props.user.user)
                ? `demo_site_${datum.SiteID}`
                : datum.SiteName,
              Active: true,
              SubCategory: publisherHasCustomBidderTiers(this.props.session.publisher)
                ? `Tier ${datum.BidderTier} - ` +
                  convertBidderTierToCustom(this.props.session.publisher, datum.BidderTier)
                : `Tier ${datum.BidderTier}`,
              Impressions: datum.Impressions,
              SiteShareOfImpressions: 0,
              eCPM: datum.AvgImpECPM,
              Revenue: datum.Revenue,
              CPMFactor: 100,
              ImpressionFactor: 100,
              EnableEditable: false,
              MarkedAsBooked: false,
            };
            id--;
            return row;
          });
        }
      }

      for (const datum of availableData) {
        datum.eCPM =
          datum.Impressions === 0 ? 0 : ((datum.Revenue || 0) / datum.Impressions) * 1000;
      }

      this.setState({
        availableData,
        availableColumns,
      });
    };

    adjustAndSumForecastData = (
      data: IForecastRowData[],
      tableName: string,
    ): IForecastRowData[] => {
      const clonedData = cloneDeep(data);
      let totalRowImpressionFactor = this.state.totalBookedImpressionFactor;
      let totalRowCPMFactor = this.state.totalBookedCPMFactor;
      if (tableName === AvailableTable) {
        totalRowImpressionFactor = this.state.totalAvailableImpressionFactor;
        totalRowCPMFactor = this.state.totalAvailableCPMFactor;
      }
      let totalRevenue = 0,
        totalImpressions = 0;

      // adjust each row by the total factor (the row's factor and the total row's factor) and sum the revenue / impressions
      for (const datum of clonedData) {
        const totalImpressionFactor = (totalRowImpressionFactor * datum.ImpressionFactor) / 100;
        const totalCPMFactor = (totalRowCPMFactor * (datum.CPMFactor || 100)) / 100;

        if (datum.Revenue !== undefined) {
          datum.Revenue = datum.Revenue * (totalCPMFactor / 100) * (totalImpressionFactor / 100);
        }
        datum.Impressions = Math.round(datum.Impressions * (totalImpressionFactor / 100));
        if (datum.eCPM !== undefined && datum.Revenue !== undefined) {
          datum.eCPM = (datum.Revenue / datum.Impressions) * 1000;
        }
        totalRevenue += datum.Revenue || 0;
        totalImpressions += datum.Impressions;
      }

      if (tableName === AvailableTable) {
        clonedData.unshift({
          ID: clonedData.length,
          Name: 'All Channels',
          SiteName: 'All Channels',
          Active: true,
          SubCategory: 'All Sub Categories',
          Impressions: Math.round(totalImpressions),
          SiteShareOfImpressions: 0,
          eCPM: totalImpressions === 0 ? 0 : (totalRevenue * 1000) / totalImpressions,
          Revenue: totalRevenue,
          CPMFactor: totalRowCPMFactor,
          ImpressionFactor: totalRowImpressionFactor,
          EnableEditable: false,
          MarkedAsBooked: undefined,
          BoldRow: true,
        });
      } else {
        clonedData.unshift({
          ID: clonedData.length,
          Name: 'All Channels',
          SiteName: 'All Channels',
          Active: true,
          SubCategory: 'All Sub Categories',
          Impressions: Math.round(totalImpressions),
          SiteShareOfImpressions: 0,
          eCPM: totalImpressions === 0 ? 0 : (totalRevenue * 1000) / totalImpressions,
          Revenue: totalRevenue,
          CPMFactor: totalRowCPMFactor,
          ImpressionFactor: totalRowImpressionFactor,
          EnableEditable: false,
          MarkedAsBooked: undefined,
          BoldRow: true,
        });
      }
      return clonedData;
    };

    renderAvailableTable = () => {
      const { availableData, availableColumns, updating } = this.state;
      const adjustedData = this.adjustAndSumForecastData(availableData, AvailableTable);

      return (
        <Grid item style={{ width: '90%' }}>
          <ListTable
            title=''
            columns={availableColumns}
            data={adjustedData}
            isLoading={updating}
            renderCustomToolbar={this.renderAvailableToolbar}
            publisher={this.props.session.publisher}
            handleChangeEditable={noopVoid}
            handleChangeItem={(
              field: string,
              id_str: string,
              event: React.ChangeEvent<HTMLInputElement>,
            ) => this.handleChangeItem(field, id_str, event, AvailableTable)}
            isInventoryForecastView={true}
          />
        </Grid>
      );
    };

    // Generate the data for the "Estimated Booked Impressions" panel.
    generateBookedDataAndColumns = (): void => {
      // get variables from the state
      const { forecastInventoryResponse, bookedGroupBy } = this.state;

      // define all the columns appearing in the table
      let bookedColumns: IListTableColumn[] = [
        { field: 'SiteName', title: 'Site Name', renderType: 'normal', width: '15%' },
        { field: 'SubCategory', title: 'Sub Category', renderType: 'normal', width: '15%' },
        { field: 'MarkedAsBooked', title: 'Booked', renderType: 'markedflag' },
        { field: 'Impressions', title: 'Impressions', renderType: 'bignumber' },
        {
          field: 'SiteShareOfImpressions',
          title: 'Site Share of Impressions',
          renderType: 'percent',
        },
        { field: 'eCPM', title: 'eCPM', renderType: 'currencyOrUndefined' },
        { field: 'Revenue', title: 'Revenue', renderType: 'currencyOrUndefined' },
      ];

      // bookedData stores all the data displayed in the table
      let bookedData = [] as IForecastRowData[];

      // when grouping by line item or site, use a map to aggregate, then convert map to array at the end
      if (forecastInventoryResponse.Analytics) {
        if (bookedGroupBy === GroupByLineItem) {
          // ######## GROUP-BY LINE ITEM ########

          // dedup map used to first store the data, will be converted to array after
          const lineItemMap = {} as IForecastMap;

          // redefines the columns to display when we do group-by line-item
          bookedColumns = [
            { field: 'SiteName', title: 'Site Name', renderType: 'normal', width: '15%' },
            { field: 'SubCategory', title: 'Sub Category', renderType: 'normal', width: '15%' },
            { field: 'MarkedAsBooked', title: 'Booked', renderType: 'markedflag' },
            { field: 'Impressions', title: 'Impressions', renderType: 'bignumber' },
            { field: 'eCPM', title: 'eCPM', renderType: 'currency' },
            { field: 'Revenue', title: 'Revenue', renderType: 'currency' },
          ];

          // populate the lineItemMap
          for (const siteID of Object.keys(
            forecastInventoryResponse.SiteCampaignBookedImpressionsMap,
          )) {
            // for each site
            if (forecastInventoryResponse.SiteCampaignBookedImpressionsMap[siteID]) {
              for (const campaignID of Object.keys(
                forecastInventoryResponse.SiteCampaignBookedImpressionsMap[siteID],
              )) {
                // for each line-item

                // get the data for the current line-item in the current site
                const bookedImpressionInfo = forecastInventoryResponse
                  .SiteCampaignBookedImpressionsMap[siteID][
                  campaignID
                ] as IBookedCampaignImpressionInfo;

                // check if the line-item is already in the map
                if (lineItemMap[campaignID]) {
                  // if the line-item is already in the map, add the current site's data to it
                  lineItemMap[campaignID].Impressions += bookedImpressionInfo.BookedImpressions;
                  if (lineItemMap[campaignID].Revenue !== undefined) {
                    // @ts-ignore | we checked not undefined above in the `if` but the lint is not smart enough
                    lineItemMap[campaignID].Revenue +=
                      (bookedImpressionInfo.CPM * bookedImpressionInfo.BookedImpressions) / 1000;
                  }
                } else {
                  // if the line-item is not in the map, add it to the map
                  lineItemMap[campaignID] = {
                    ID: 0,
                    Name: '',
                    SiteName: 'All Channels',
                    Active: true,
                    SubCategory: 'Line Item - ' + bookedImpressionInfo.CampaignName,
                    Impressions: bookedImpressionInfo.BookedImpressions,
                    SiteShareOfImpressions: 0,
                    eCPM: 0,
                    Revenue:
                      (bookedImpressionInfo.CPM * bookedImpressionInfo.BookedImpressions) / 1000,
                    CPMFactor: 100,
                    ImpressionFactor: 100,
                    EnableEditable: false,
                    MarkedAsBooked: true,
                  } as IForecastRowData;
                }
              }
            }
          }

          // convert the map to an array
          bookedData = Object.values(lineItemMap)
            .filter((row) => row.Impressions > 0)
            .map((row, i) => {
              row.ID = i;
              return row;
            });
        } else if (bookedGroupBy === GroupByChannel) {
          // ######## GROUP-BY SITE ########

          // dedup map used to first store the data, will be converted to array after
          const siteMap = {} as IForecastMap;

          // redefines the columns to display when we do group-by site
          bookedColumns = [
            { field: 'SiteName', title: 'Site Name', renderType: 'normal', width: '15%' },
            { field: 'SubCategory', title: 'Sub Category', renderType: 'normal', width: '15%' },
            { field: 'MarkedAsBooked', title: 'Booked', renderType: 'markedflag' },
            { field: 'Impressions', title: 'Impressions', renderType: 'bignumber' },
            { field: 'eCPM', title: 'eCPM', renderType: 'currencyOrUndefined' },
            { field: 'Revenue', title: 'Revenue', renderType: 'currencyOrUndefined' },
          ];

          // populate the siteMap...

          // create the forecast row data for the current site
          const forecastRow = {
            ID: 0,
            Name: '',
            SiteName: '',
            Active: true,
            SubCategory: '',
            Impressions: 0,
            SiteShareOfImpressions: 0,
            eCPM: 0,
            Revenue: 0,
            CPMFactor: 100,
            ImpressionFactor: 100,
            EnableEditable: false,
            MarkedAsBooked: true,
          } as IForecastRowData;

          // for each site that contains line-items
          for (const siteID of Object.keys(
            forecastInventoryResponse.SiteCampaignBookedImpressionsMap,
          )) {
            // add the data for the current site's line-item(s) into the map
            siteMap[siteID + '_line-item'] = { ...forecastRow }; // copy object, instead of reference

            // if line-item exist for this site
            if (forecastInventoryResponse.SiteCampaignBookedImpressionsMap[siteID]) {
              for (const campaignID of Object.keys(
                forecastInventoryResponse.SiteCampaignBookedImpressionsMap[siteID],
              )) {
                // for each line-item

                // get the data for the current line-item in the current site
                const bookedImpressionInfo = forecastInventoryResponse
                  .SiteCampaignBookedImpressionsMap[siteID][
                  campaignID
                ] as IBookedCampaignImpressionInfo;

                // add the data for the current line-item to this site's data
                siteMap[siteID + '_line-item'].Impressions +=
                  bookedImpressionInfo.BookedImpressions;
                if (siteMap[siteID + '_line-item'].Revenue !== undefined) {
                  // @ts-ignore | we checked not undefined above in the `if` but the lint is not smart enough
                  siteMap[siteID + '_line-item'].Revenue +=
                    (bookedImpressionInfo.CPM * bookedImpressionInfo.BookedImpressions) / 1000;
                }
                siteMap[siteID + '_line-item'].SiteName = bookedImpressionInfo.SiteName;
                siteMap[siteID + '_line-item'].SubCategory = 'All Line Items';
              }
            }
          }

          // for each site that contains deals
          for (const siteID of Object.keys(
            forecastInventoryResponse.SiteDealBookedImpressionsMap,
          )) {
            // add the data for the current site's PG deal(s) into the map
            siteMap[siteID + '_deal'] = {
              ...forecastRow, // copy forecast object
              eCPM: undefined, // not applicable for deals
              Revenue: undefined, // not applicable for deals
              CPMFactor: undefined, // not applicable for deals
            };

            // if PG deal exist for this site
            if (forecastInventoryResponse.SiteDealBookedImpressionsMap[siteID]) {
              for (const dealID of Object.keys(
                forecastInventoryResponse.SiteDealBookedImpressionsMap[siteID],
              )) {
                // for each PG deal

                // get the data for the current PG deal in the current site
                const bookedImpressionInfo = forecastInventoryResponse.SiteDealBookedImpressionsMap[
                  siteID
                ][dealID] as IBookedDealImpressionInfo;

                // add the data for the current PG deal to this site's data
                siteMap[siteID + '_deal'].Impressions += bookedImpressionInfo.BookedImpressions;
                siteMap[siteID + '_deal'].SiteName = bookedImpressionInfo.SiteName;
                siteMap[siteID + '_deal'].SubCategory = 'All PG Deals';
              }
            }
          }

          bookedData = Object.values(siteMap)
            .filter((row) => row.Impressions > 0)
            .map((row, i) => {
              row.ID = i;
              return row;
            });
        } else if (bookedGroupBy === GroupByPGDeal) {
          // ######## GROUP-BY DEAL ########

          // dedup map used to first store the data, will be converted to array after
          const dealMap = {} as IForecastMap;

          // redefines the columns to display when we do group-by deals
          bookedColumns = [
            { field: 'SiteName', title: 'Site Name', renderType: 'normal', width: '15%' },
            { field: 'SubCategory', title: 'Sub Category', renderType: 'normal', width: '15%' },
            { field: 'MarkedAsBooked', title: 'Booked', renderType: 'markedflag' },
            { field: 'Impressions', title: 'Impressions', renderType: 'bignumber' },
          ];

          // populate the dealMap
          for (const siteID of Object.keys(
            forecastInventoryResponse.SiteDealBookedImpressionsMap,
          )) {
            // for each site

            if (forecastInventoryResponse.SiteDealBookedImpressionsMap[siteID]) {
              for (const dealID of Object.keys(
                forecastInventoryResponse.SiteDealBookedImpressionsMap[siteID],
              )) {
                // for each deal

                // get the data for the current deal in the current site
                const bookedImpressionInfo = forecastInventoryResponse.SiteDealBookedImpressionsMap[
                  siteID
                ][dealID] as IBookedDealImpressionInfo;

                // check if the deal is already in the map
                if (dealMap[dealID]) {
                  // if the deal is already in the map, add the current site's data to it
                  dealMap[dealID].Impressions += bookedImpressionInfo.BookedImpressions;
                } else {
                  // if the deal is not in the map, add it to the map
                  dealMap[dealID] = {
                    ID: 0,
                    Name: '',
                    SiteName: 'All Channels',
                    Active: true,
                    SubCategory: 'Deal - ' + bookedImpressionInfo.DealName,
                    Impressions: bookedImpressionInfo.BookedImpressions,
                    SiteShareOfImpressions: 0,
                    ImpressionFactor: 100,
                    EnableEditable: false,
                    MarkedAsBooked: true,
                  } as IForecastRowData;
                }
              }
            }
          }

          // convert the map to an array
          bookedData = Object.values(dealMap)
            .filter((row) => row.Impressions > 0)
            .map((row, i) => {
              row.ID = i;
              return row;
            });
        } else if (bookedGroupBy === GroupByNone) {
          // ######## GROUP-BY NONE ########
          let id = 0;

          // handle line-items
          // for each campaign channel
          for (const siteID of Object.keys(
            forecastInventoryResponse.SiteCampaignBookedImpressionsMap,
          )) {
            if (forecastInventoryResponse.SiteCampaignBookedImpressionsMap[siteID]) {
              for (const campaignID of Object.keys(
                forecastInventoryResponse.SiteCampaignBookedImpressionsMap[siteID],
              )) {
                // for each line-item

                // get the data for the current line-item in the current channel
                const bookedImpressionInfo = forecastInventoryResponse
                  .SiteCampaignBookedImpressionsMap[siteID][
                  campaignID
                ] as IBookedCampaignImpressionInfo;

                // create the forecast row object from the line-item data
                const datum = {
                  ID: id,
                  Name: '',
                  SiteName: checkUserInfoMasked(this.props.user.user)
                    ? `demo_site_${id}`
                    : bookedImpressionInfo.SiteName,
                  Active: true,
                  SubCategory: 'Line Item - ' + bookedImpressionInfo.CampaignName,
                  Impressions: bookedImpressionInfo.BookedImpressions,
                  SiteShareOfImpressions: bookedImpressionInfo.SiteShareOfImpressions * 100,
                  eCPM: bookedImpressionInfo.CPM,
                  Revenue:
                    (bookedImpressionInfo.CPM * bookedImpressionInfo.BookedImpressions) / 1000,
                  CPMFactor: 100,
                  ImpressionFactor: 100,
                  EnableEditable: false,
                  MarkedAsBooked: true,
                } as IForecastRowData;

                // if the line-item made some impressions, add it to the displayed data
                if (datum.Impressions > 0) {
                  bookedData.push(datum);
                  id++;
                }
              }
            }
          }

          // handle PG deals
          for (const siteID of Object.keys(
            forecastInventoryResponse.SiteDealBookedImpressionsMap,
          )) {
            if (forecastInventoryResponse.SiteDealBookedImpressionsMap[siteID]) {
              for (const dealID of Object.keys(
                forecastInventoryResponse.SiteDealBookedImpressionsMap[siteID],
              )) {
                // for each PG deal

                // get the data for the current deal in the current channel
                const bookedImpressionInfo = forecastInventoryResponse.SiteDealBookedImpressionsMap[
                  siteID
                ][dealID] as IBookedDealImpressionInfo;

                // create the forecast row object from the PG deal data
                const datum = {
                  ID: id,
                  Name: '',
                  SiteName: bookedImpressionInfo.SiteName,
                  Active: true,
                  SubCategory: 'Deal - ' + bookedImpressionInfo.DealName,
                  Impressions: bookedImpressionInfo.BookedImpressions,
                  SiteShareOfImpressions: bookedImpressionInfo.SiteShareOfImpressions * 100,
                  eCPM: undefined, // not applicable to deals
                  Revenue: undefined, // not applicable to deals
                  CPMFactor: undefined, // not applicable to deals
                  ImpressionFactor: 100,
                  EnableEditable: false,
                  MarkedAsBooked: true,
                } as IForecastRowData;

                // if the deal made some impressions, add it to the displayed data
                if (datum.Impressions > 0) {
                  bookedData.push(datum);
                  id++;
                }
              }
            }
          }
        }
      }

      for (const datum of bookedData) {
        if (datum.eCPM !== undefined) {
          datum.eCPM =
            datum.Impressions === 0 ? 0 : ((datum.Revenue || 0) / datum.Impressions) * 1000;
        }
      }

      bookedData.sort(
        (a: IForecastRowData, b: IForecastRowData) => (b.Revenue || 0) - (a.Revenue || 0),
      );

      // if we are not grouping by deals, add the CPM factor column
      if (bookedGroupBy !== GroupByPGDeal) {
        bookedColumns.push({
          field: 'CPMFactor',
          title: 'CPM Adjustment%',
          renderType: 'editable',
        });
      }

      // add the impression factor column, available for both line-items and deals
      bookedColumns.push({
        field: 'ImpressionFactor',
        title: 'Impression Adjustment%',
        renderType: 'editable',
      });

      this.setState({
        bookedData,
        bookedColumns,
      });
    };

    renderBookedToolbar = (props: any) => {
      const { bookedGroupBy, forecastDuration } = this.state;
      const { classes } = this.props;
      const copyProps = {
        ...props,
        searchFieldStyle: { ...props.searchFieldStyle, width: 250, marginRight: 15 },
      };

      return (
        <Grid item style={{ width: '100%' }}>
          {/* Alert on channel without any impressions*/}
          <p
            style={{
              paddingTop: '25px',
              fontSize: '15px',
              marginLeft: '10px',
            }}
          >
            Only channels with impressions are available for forecasting.
          </p>
          {/* Estimated booked impressions table*/}
          <Grid container direction='row' justify='space-between' alignItems='center'>
            <Grid item>
              <Grid container direction='row' spacing={2} alignItems='center'>
                <Grid item>
                  <Typography className={classes.tableTitle}>
                    Estimated Booked Impressions
                  </Typography>
                </Grid>
                <Grid item>
                  <ChipCompleted clickable={false} label={`Next ${forecastDuration} Days`} />
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container alignItems='center' spacing={2}>
                <Grid item>
                  <Typography className={classes.boldText}>Group By</Typography>
                </Grid>
                <Grid item>
                  <Select
                    value={bookedGroupBy}
                    name='bookedGroupBy'
                    onChange={(ev: React.ChangeEvent<{ name: string; value: string }>) =>
                      this.handleChangeGroupBy(ev, BookedTable)
                    }
                  >
                    {[GroupByNone, GroupByLineItem, GroupByPGDeal, GroupByChannel].map((type) => {
                      return (
                        <MenuItem value={type}>
                          <Typography style={{ fontSize: 14 }}>{type}</Typography>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <MTableToolbar {...copyProps} />
            </Grid>
          </Grid>
        </Grid>
      );
    };

    renderBookedImpressionsTable = () => {
      const { bookedData, bookedColumns, updating } = this.state;
      const adjustedData = this.adjustAndSumForecastData(bookedData, BookedTable);
      return (
        <Grid item style={{ width: '90%' }}>
          <ListTable
            title=''
            columns={bookedColumns}
            data={adjustedData}
            isLoading={updating}
            renderCustomToolbar={this.renderBookedToolbar}
            publisher={this.props.session.publisher}
            handleChangeEditable={noopVoid}
            handleChangeItem={(
              field: string,
              id_str: string,
              event: React.ChangeEvent<HTMLInputElement>,
            ) => this.handleChangeItem(field, id_str, event, BookedTable)}
            isInventoryForecastView={true}
          />
        </Grid>
      );
    };

    renderSqlTextBox(text: string) {
      return (
        <Editor
          placeholder={''}
          value={text}
          highlight={(code) => highlight(code, languages.sql, 'sql')}
          padding={0}
          maxLength={1000}
          onValueChange={() => null}
          readOnly={true}
        />
      );
    }

    render() {
      const {
        snackbarMessage,
        snackbarError,
        updating,
        loading,
        channelGroupsMenuList,
        channelsMenuList,
      } = this.state;
      const { classes, user, session, location } = this.props;
      const { pathname } = location;
      let channelSelected = false;
      for (const item of Object.values(channelGroupsMenuList)) {
        if (item.selected) {
          channelSelected = true;
        }
        if (channelSelected) {
          break;
        }
      }
      for (const item of Object.values(channelsMenuList)) {
        if (item.selected) {
          channelSelected = true;
        }
        if (channelSelected) {
          break;
        }
      }
      if (loading) {
        return <LoadingView active={true} text='Loading the Forecasting Tool' />;
      }
      return (
        <Panels maxWidth vertical relative minWidthMain>
          <Panel
            largeHorizontalPadding
            verticalPadding
            style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
          >
            <ModelTopBar
              title={routeName(pathname)}
              session={session}
              user={user}
              publisher={session.publisher}
              classes={classes}
              delegateElevate={true}
              currentPath={pathname}
              disableTitlePaddingLeft
            />
          </Panel>
          <Panel scroll largePadding>
            {this.renderForecastSettings()}
            <Grid item style={{ maxWidth: 700 }}>
              <Grid
                container
                direction='row'
                alignItems='center'
                justify='space-between'
                spacing={2}
              >
                <Grid item>
                  <Tooltip
                    title={
                      channelSelected
                        ? ''
                        : 'Select channel(s) or channel group to run the forecast'
                    }
                  >
                    <span>
                      <Button
                        disabled={updating || !channelSelected}
                        onClick={this.getInventoryForecast}
                        className={this.props.classes.button}
                      >
                        Run Forecast
                      </Button>
                    </span>
                  </Tooltip>
                </Grid>
              </Grid>
            </Grid>
            <Grid item style={{ width: '100%' }}>
              <Divider />
            </Grid>
            <Grid item style={{ width: '90%' }}>
              <Typography style={{ fontSize: 14 }}>
                Use the flag to mark any row as booked and subtract it from available impressions.
                Use % adjustment on CPM and Impressions to fine tune your forecast as needed.
              </Typography>
            </Grid>
            {this.renderAvailableTable()}
            {this.renderBookedImpressionsTable()}
            {this.state.forecastQuery !== '' && hasRoleSuperUser(this.props.user.user) && (
              <Paper style={{ width: '90%', padding: '10px' }}>
                <Grid>
                  <Typography>
                    <strong>Forecasting Queries *</strong>
                  </Typography>
                  {this.renderSqlTextBox(this.state.forecastQuery)}
                </Grid>
              </Paper>
            )}
            <SnackbarView
              open={!!snackbarMessage}
              error={!!snackbarError}
              onClose={this.handleCloseSnackbar}
            >
              {snackbarMessage}
            </SnackbarView>
          </Panel>
        </Panels>
      );
    }
  },
);
